import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { useTracking } from "@context/Tracking";
import useGiftPackages from "@hooks/useGiftPackages";
import { cn } from "@helpers/general";
import { isMTOOnlyCart } from "@helpers/cart";

import ChoosePackaging from "@components/sideCart/giftPackagingPopup/ChoosePackaging";
import GiftNote from "@components/sideCart/giftPackagingPopup/GiftNote";
import GiftPackagingWrapper from "@components/sideCart/GiftPackagingWrapper";

import IconArrow from "@svgs/arrow-br.svg";
import IconClose from "@svgs/close-br.svg";

function GiftPackagingPopup({ onClose, lines, cartNote, checkoutUrl, giftPackagesData }) {
  const [step, setStep] = useState(1);
  const [selectedPackaging, setSelectedPackaging] = useState(null);

  const { trackGiftPackagingActions } = useTracking();
  const { products: productsWithPrices, isInitialLoading: isLoadingGiftPackages } =
    useGiftPackages();

  const packageInCart = lines.find((line) =>
    productsWithPrices.some((product) => product.handle === line.variant.product.handle)
  );
  const packageInCartHandle = packageInCart?.variant?.product?.handle;

  const isMtoOnlyCart = isMTOOnlyCart({ lines });

  useEffect(() => {
    if (!packageInCartHandle || selectedPackaging) {
      return;
    }

    const cartPackage = productsWithPrices.find(
      (product) => product.handle === packageInCartHandle
    );

    if (cartPackage) {
      setSelectedPackaging(cartPackage);
    }
  }, [packageInCartHandle, productsWithPrices, selectedPackaging]);

  const handleContinueClick = () => {
    trackGiftPackagingActions({
      action: "continue_button_click",
      productId: selectedPackaging?.id,
    });
    setStep(2);
  };

  const handlePopupClose = () => {
    trackGiftPackagingActions({ action: "close" });
    onClose();
  };

  return (
    <GiftPackagingWrapper>
      <div className="w-full flex justify-between items-center py-[18px] px-[15px] desktop:p-[25px] text-center border-b border-dark-grey h-[55px] desktop:h-[60px]">
        <button
          type="button"
          onClick={() => setStep(1)}
          className={cn("invisible", { visible: step === 2 })}
        >
          <IconArrow width={16} className="rotate-[90deg]" />
        </button>

        <p className="typo-body">Step {step} of 2</p>
        <IconClose
          width={15}
          height={15}
          onClick={handlePopupClose}
          className="cursor-pointer"
          data-testid="close-icon"
        />
      </div>
      <div className="flex-1 flex flex-col px-[15px] pt-[20px] pb-[15px] desktop:p-[25px]">
        {step === 1 && (
          <ChoosePackaging
            onContinueClick={handleContinueClick}
            selectedPackaging={selectedPackaging}
            setSelectedPackaging={setSelectedPackaging}
            giftPackagingData={isMtoOnlyCart ? giftPackagesData.MTO : giftPackagesData.CLASSIC}
            products={productsWithPrices}
            isLoading={isLoadingGiftPackages}
          />
        )}
        {step === 2 && (
          <GiftNote
            closePopup={handlePopupClose}
            selectedPackaging={selectedPackaging}
            cartNote={cartNote}
            packageInCart={packageInCart}
            checkoutUrl={checkoutUrl}
          />
        )}
      </div>
    </GiftPackagingWrapper>
  );
}

GiftPackagingPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      variant: PropTypes.shape({
        product: PropTypes.shape({
          handle: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    })
  ).isRequired,
  cartNote: PropTypes.string,
  checkoutUrl: PropTypes.string.isRequired,
};

export default GiftPackagingPopup;
